import type { BodyHandle, ShapeHandle, World } from 'box3d-web';
import type { DemolitionNativeParticipant, DemolitionRuntime } from './types';

export function destroyOwnedBody(rt: DemolitionRuntime, body: BodyHandle): void {
  let firstError: unknown;
  try {
    rt.world.destroyBody(body);
  } catch (error) {
    firstError = error;
  } finally {
    try {
      rt.ownership.unregisterBody(body);
    } catch (error) {
      if (firstError === undefined) firstError = error;
    }
  }
  if (firstError !== undefined) throw firstError;
}

type BodyCreateOptions = Parameters<World['createBody']>[0];
type BoxHalfExtents = Parameters<World['addBox']>[1];
type BoxOptions = Parameters<World['addBox']>[2];
type SphereOptions = Parameters<World['addSphere']>[2];
type HullOptions = Parameters<World['addHull']>[2];

export function createOwnedBody(
  rt: DemolitionRuntime,
  options: BodyCreateOptions,
  participant: DemolitionNativeParticipant,
): BodyHandle {
  const body = rt.world.createBody(options);
  try {
    rt.ownership.registerBody(body, participant);
    return body;
  } catch (error) {
    try { destroyOwnedBody(rt, body); } catch { /* preserve registration failure */ }
    throw error;
  }
}

export function registerOwnedShape(
  rt: DemolitionRuntime,
  shape: ShapeHandle,
  body: BodyHandle,
  participant: DemolitionNativeParticipant,
): ShapeHandle {
  rt.ownership.registerShape(shape, body, participant);
  return shape;
}

export function addOwnedBox(
  rt: DemolitionRuntime,
  body: BodyHandle,
  halfExtents: BoxHalfExtents,
  options: BoxOptions,
  participant: DemolitionNativeParticipant,
): ShapeHandle {
  try {
    return registerOwnedShape(rt, rt.world.addBox(body, halfExtents, options), body, participant);
  } catch (error) {
    try { destroyOwnedBody(rt, body); } catch { /* preserve creation failure */ }
    throw error;
  }
}

export function addOwnedSphere(
  rt: DemolitionRuntime,
  body: BodyHandle,
  radius: number,
  options: SphereOptions,
  participant: DemolitionNativeParticipant,
): ShapeHandle {
  try {
    return registerOwnedShape(rt, rt.world.addSphere(body, radius, options), body, participant);
  } catch (error) {
    try { destroyOwnedBody(rt, body); } catch { /* preserve creation failure */ }
    throw error;
  }
}

export function addOwnedHull(
  rt: DemolitionRuntime,
  body: BodyHandle,
  points: Parameters<World['addHull']>[1],
  options: HullOptions,
  participant: DemolitionNativeParticipant,
): ShapeHandle {
  try {
    return registerOwnedShape(rt, rt.world.addHull(body, points, options), body, participant);
  } catch (error) {
    try { destroyOwnedBody(rt, body); } catch { /* preserve creation failure */ }
    throw error;
  }
}
